import Dexie, { type EntityTable } from 'dexie';
import { type EncryptedData, decrypt, encrypt, isEncryptionAvailable } from './crypto';

/**
 * Pending mutation queued while offline.
 * Processed by SyncManager when connectivity is restored.
 */
export interface SyncQueueEntry {
  id?: number;
  entityType: string;
  entityId: string;
  action: 'create' | 'update' | 'delete';
  payload: string; // JSON-serialized mutation payload
  createdAt: number; // Date.now() timestamp
  syncedAt: number | null;
  retryCount: number;
}

/**
 * Cached entity for offline read access.
 * Keyed by [entityType, entityId] compound index.
 */
export interface OfflineCacheEntry {
  id?: number;
  entityType: string;
  entityId: string;
  data: string; // JSON-serialized entity data (or base64 ciphertext when encrypted)
  iv: string | null; // base64-encoded AES-GCM IV (null = unencrypted)
  cachedAt: number; // Date.now() timestamp
  expiresAt: number; // Date.now() + TTL
}

/**
 * Cached password hash for offline login.
 */
export interface CredentialCacheEntry {
  id?: number;
  email: string;
  hash: string; // base64-encoded PBKDF2 hash
  salt: string; // base64-encoded random salt
  storedAt: number; // Date.now() timestamp
  userId: string;
}

/**
 * Audit log entry for offline operations (compliance).
 */
export interface AuditLogEntry {
  id?: number;
  userId: string;
  action: string;
  entityType: string;
  entityId: string;
  detail: string | null; // JSON-serialized additional context
  timestamp: number; // Date.now()
  syncedAt: number | null; // null = pending, timestamp = synced to server
}

class ZoobookOfflineDB extends Dexie {
  syncQueue!: EntityTable<SyncQueueEntry, 'id'>;
  offlineCache!: EntityTable<OfflineCacheEntry, 'id'>;
  credentialCache!: EntityTable<CredentialCacheEntry, 'id'>;
  auditLog!: EntityTable<AuditLogEntry, 'id'>;

  constructor() {
    super('zoobook-offline');

    this.version(1).stores({
      syncQueue: '++id, entityType, entityId, action, createdAt, syncedAt, retryCount',
      offlineCache: '++id, [entityType+entityId], entityType, cachedAt, expiresAt',
    });

    this.version(2).stores({
      syncQueue: '++id, entityType, entityId, action, createdAt, syncedAt, retryCount',
      offlineCache: '++id, [entityType+entityId], entityType, cachedAt, expiresAt',
      credentialCache: '++id, email, userId, storedAt',
      auditLog: '++id, userId, entityType, timestamp, syncedAt',
    });
  }
}

export const db = new ZoobookOfflineDB();

// ---------------------------------------------------------------------------
// Cache helpers with transparent encryption
// ---------------------------------------------------------------------------

const DEFAULT_TTL_MS = 24 * 3600_000; // 24 hours

/**
 * Cache an entity with optional encryption (based on whether a key is available).
 */
export async function cacheEntity(
  entityType: string,
  entityId: string,
  data: Record<string, unknown>,
  ttlMs = DEFAULT_TTL_MS,
): Promise<void> {
  const now = Date.now();
  let serialized: string;
  let iv: string | null = null;

  if (isEncryptionAvailable()) {
    const encrypted: EncryptedData = await encrypt(JSON.stringify(data));
    serialized = encrypted.ciphertext;
    iv = encrypted.iv;
  } else {
    serialized = JSON.stringify(data);
  }

  const existing = await db.offlineCache
    .where('[entityType+entityId]')
    .equals([entityType, entityId])
    .first();

  const entry: Omit<OfflineCacheEntry, 'id'> = {
    entityType,
    entityId,
    data: serialized,
    iv,
    cachedAt: now,
    expiresAt: now + ttlMs,
  };

  if (existing?.id != null) {
    await db.offlineCache.update(existing.id, entry);
  } else {
    await db.offlineCache.add(entry as OfflineCacheEntry);
  }
}

/**
 * Retrieve a cached entity, decrypting if needed.
 * Returns null if not found, expired, or decryption fails.
 */
export async function getCachedEntity(
  entityType: string,
  entityId: string,
): Promise<Record<string, unknown> | null> {
  const entry = await db.offlineCache
    .where('[entityType+entityId]')
    .equals([entityType, entityId])
    .first();

  if (!entry) return null;
  if (entry.expiresAt < Date.now()) return null;

  try {
    if (entry.iv) {
      // Encrypted entry — decrypt
      const plaintext = await decrypt({ iv: entry.iv, ciphertext: entry.data });
      return JSON.parse(plaintext);
    }
    // Unencrypted entry
    return JSON.parse(entry.data);
  } catch {
    // Decryption or parse failed (key unavailable or data corrupted)
    return null;
  }
}
